import _ from 'lodash';
import {
  Country,
  State as StateEnum,
  UserRequirementsInfo,
  TradeAuthorizationType,
  ExperienceYearType,
  BeneficiaryAccountType,
  BeneficiaryModel,
} from '@tradingblock/types';
import {
  AllApplicationSteps,
  AllCharacteristicsKeys,
  ExperienceKey,
  ApplicationModel,
  PhoneModel,
  ApplicationStep,
  BooleanToggleType,
  ErrorType,
  ApplicationType,
  EmploymentType,
  BeneficiaryType,
  PersonType,
  DateModel,
  DataValue,
  RE_EMAIL,
  RE_SSN,
  RE_EIN,
  RE_PO_BOX,
  TotalSecurityChallenges,
  EntityAccountType,
  DefaultAccountHolder,
  AccountHolderModel,
  ForeignDueDiligenceModel,
  AccountDisclosureKey,
  AccountHolderDisclosureKey,
  AllAccountHolderDisclosureKeys,
  AllEntityAccountDisclosureKeys,
  AllEntityNonTrustAccountDisclosureKeys,
  TradeGoalType,
  AccountHolderField,
  DefaultForeignDueDiligence,
  RetirementAccountType,
  RE_VALID_SUFFIX,
  RE_PREFIX_PATTERN,
  JointAccountType,
  ASCII_AUDIT_RANGE,
  RE_POSTAL_CODE,
  isValidPersonName,
} from '../types';
import {
  getOwnerNamesForSignStep,
  getDateFromModel,
  getAllExperienceKeysForApplication,
  isUserNameValid,
  isPasswordValid,
} from './';
import {
  getAllAgreementKeysForApplication,
  showBeneficiariesFields,
  showOwnersOfficersFields,
  showTrustedContactFields,
  showTenantsInCommonInterestPercentFields,
} from './Form';
import config from '../config';
import dayjs from 'dayjs';
import { isForeignAccount } from '@tradingblock/components';
import { DataStore } from '../state/store';

export const isValidDate = (date: DateModel) => {
  const dayjs = getDateFromModel(date);
  // need to compare dayjs parts to original date parts, since dayjs.isValid doesn't check for non-existant dates (e.g. Feb 31)
  return (
    dayjs &&
    dayjs.year() === _.toNumber(date.year) &&
    dayjs.month() + 1 === _.toNumber(date.month) && // like Date object, month is zero-indexed
    dayjs.date() === _.toNumber(date.day)
  );
};

const getError = (value: DataValue, error: ErrorType) => {
  if (!value && !_.isNumber(value)) {
    return error;
  }
  return undefined;
};

const getDuplicateError = (value: DataValue, message: string | string[] | undefined) => {
  if (!value && !_.isNumber(value)) {
    return message;
  }
  return undefined;
};

const getRequiredError = (value: DataValue) => {
  return getError(value, ErrorType.Required);
};

const getInvalidError = (value: DataValue) => {
  return getError(value, ErrorType.Invalid);
};

const getDuplicateAccountError = (value: DataValue, message?: string | string[]) => {
  return getDuplicateError(value, message);
};

const getAmountError = (value: DataValue) => {
  return getError(value, ErrorType.Amount);
};

const getMismatchError = (value: DataValue) => {
  return getError(value, ErrorType.Mismatch);
};

const getEmailError = (value: string | null | undefined) => {
  return getRequiredError(value) || getInvalidError(value && RE_EMAIL.test(value));
};

const getInvalidDateError = (value: DateModel | undefined) => {
  return value && getInvalidError(isValidDate(value));
};

const getContingentError = (value: DataValue) => {
  return getError(value, ErrorType.Contingent);
};

const getInvalidLengthError = (value: DataValue) => {
  return getError(value, ErrorType.InvalidLength);
};

const getInvalidLegalNameError = (value: DataValue) => {
  return getError(value, ErrorType.InvalidLegalName);
};

export const validateApplicationStepAsync = async (
  step: ApplicationStep | undefined,
  values: ApplicationModel,
  visibleSections: number | undefined,
  requirements?: UserRequirementsInfo,
  duplicateAccountCheck?: {
    isFetching: boolean;
    error?: boolean;
    errorMessage?: string[] | string | undefined;
  }
) => {
  let errors = {};
  switch (step) {
    case ApplicationStep.SecureAccount:
      errors = validateSecureAccount(values, visibleSections, requirements);
      break;
    case ApplicationStep.ClearerType:
      errors = validateClearerType(values);
      break;
    case ApplicationStep.AccountType:
      errors = validateAccountType(values);
      break;
    case ApplicationStep.InvestingProfile:
      errors = validateInvestingProfile(values, visibleSections);
      break;
    case ApplicationStep.AccountInformation:
      errors = validateAccountInformation(values, visibleSections, duplicateAccountCheck);
      break;
    case ApplicationStep.Beneficiaries:
      errors = validateBeneficiaries(values, duplicateAccountCheck);
      break;
    case ApplicationStep.Agreements:
      errors = validateAgreements(values, visibleSections);
      break;
    case ApplicationStep.Sign:
      errors = validateSign(values);
      break;
  }

  return new Promise(resolve => resolve(_.pickBy(errors, err => !!err))).then(errs => {
    if (!_.isEmpty(errs)) {
      if (/localhost/.test(window.location.host)) {
        console.warn(`validateApplicationStepAsync :: INVALID: `, errs);
      }
      // throw errs;
    }
    return errs;
  });
};

// for use on summary to validate all steps, except 1st (secure), and optionally which final steps (agreements/sign)
export const validateApplication = (
  application: ApplicationModel,
  finalStepsToValidate: ApplicationStep[] = [],
  duplicateAccountCheck?: {
    isFetching: boolean;
    error?: boolean;
    errorMessage?: string[] | string | undefined;
  }
) => {
  const errors = _.reduce(
    AllApplicationSteps,
    (acc: { [key: string]: ErrorType | false | undefined }, step) => {
      switch (step) {
        case ApplicationStep.ClearerType:
          return {
            ...acc,
            ...validateClearerType(application),
          };
        case ApplicationStep.AccountType:
          return {
            ...acc,
            ...validateAccountType(application),
          };
        case ApplicationStep.InvestingProfile:
          return {
            ...acc,
            ...validateInvestingProfile(application, undefined),
          };
        case ApplicationStep.AccountInformation:
          return {
            ...acc,
            ...validateAccountInformation(application, undefined, duplicateAccountCheck),
          };
        case ApplicationStep.Beneficiaries:
          return {
            ...acc,
            ...validateBeneficiaries(application, duplicateAccountCheck),
          };
        case ApplicationStep.Agreements:
          if (_.includes(finalStepsToValidate, ApplicationStep.Agreements)) {
            return {
              ...acc,
              ...validateAgreements(application, undefined),
            };
          }
          return acc;
        default:
          return acc;
      }
    },
    {}
  );

  // if the application is a partialEntitySubmission then only return the errors for the ClearerType & AccountType steps
  if (application.isPartialEntitySubmission) {
    return _.pick(errors, [ApplicationStep.ClearerType, ApplicationStep.AccountType]);
  }
  return _.pickBy(errors, err => !!err);
};

const validateSecureAccount = (
  values: ApplicationModel,
  visibleSections: number | undefined,
  requirements: UserRequirementsInfo | undefined
) => {
  // Personal information validation has been moved to the home page
  // Only validate the fields that remain on the secure account step
  let errorsSection1 = {};

  // "heard about" fields not shown on virtual application
  if (!config.isVirtual) {
    errorsSection1 = {
      ...errorsSection1,
      ownerCountry: getRequiredError(values.ownerCountry),
      hearAboutUsId: getRequiredError(values.hearAboutUsId),
      repId: values.hearAboutUsId ? getRequiredError(values.repId) : undefined,
    };
  }

  // User credentials are now collected on the home page, so we don't validate them here
  // Section #2 validation has been moved to the home page

  return {
    ...errorsSection1,
  };
};

const validateClearerType = (values: ApplicationModel) => {
  return {
    clearer: getRequiredError(values.clearer),
  };
};

const validateAccountType = (values: ApplicationModel) => {
  return {
    ownerCountry: getRequiredError(values.ownerCountry), // || getInvalidError(_.includes(AcceptedCountries, values.ownerCountry)),
    ...(values.type === ApplicationType.Individual
      ? {
          retirementAccount: getRequiredError(values.retirementAccount),
          retirementAccountType:
            values.retirementAccount === BooleanToggleType.Yes && getRequiredError(values.retirementAccountType),
          simpleIraPlanName:
            values.retirementAccount === BooleanToggleType.Yes &&
            values.retirementAccountType === RetirementAccountType.Simple &&
            getRequiredError(values.simpleIraPlanName),
          beneficiaryIraDecedentName:
            values.retirementAccount === BooleanToggleType.Yes &&
            (values.retirementAccountType === RetirementAccountType.Beneficiary ||
              values.retirementAccountType === RetirementAccountType.RothBeneficiary) &&
            getRequiredError(values.beneficiaryIraDecedentName),
        }
      : undefined),
    ...(values.type === ApplicationType.Joint
      ? {
          jointAccountType: getRequiredError(values.jointAccountType),
          jointCommunityPropertyState:
            values.jointAccountType === JointAccountType.Community &&
            getRequiredError(values.jointCommunityPropertyState),
        }
      : undefined),
    ...(values.type === ApplicationType.Entity
      ? {
          entityAccountType: getRequiredError(values.entityAccountType),
        }
      : undefined),
  };
};

const validateInvestingProfile = (values: ApplicationModel, visibleSections: number | undefined) => {
  const errorsSection1 = {
    tradeGoalType: getRequiredError(values.tradeGoalType),
    investmentObjectiveType: getRequiredError(values.investmentObjectiveType),
  };

  // Section #2
  let errorsSection2 = {};
  if (!visibleSections || visibleSections > 1) {
    errorsSection2 = {
      ...(values.tradeGoalType !== TradeGoalType.StocksBonds
        ? _.reduce(
            getAllExperienceKeysForApplication(values),
            (acc: { [key: string]: string | undefined }, key: ExperienceKey) => {
              return {
                ...acc,
                [`experience.${key}.years`]: getRequiredError(
                  values.experience && values.experience[key] && values.experience[key].years
                ),
                [`experience.${key}.tradesPerYear`]: getRequiredError(
                  values.experience &&
                    values.experience[key] &&
                    values.experience[key].years &&
                    values.experience[key].years !== ExperienceYearType.Zero
                    ? values.experience[key].tradesPerYear
                    : true
                ),
              };
            },
            {}
          )
        : {}),
      annualIncome: getRequiredError(values.annualIncome),
      totalNetWorth: getRequiredError(values.totalNetWorth),
      liquidNetWorth: getRequiredError(values.liquidNetWorth),
      ..._.reduce(
        AllCharacteristicsKeys,
        (acc: { [key: string]: string | undefined }, key) => {
          return {
            ...acc,
            [`characteristics.${key}`]: getRequiredError(_.get(values, `characteristics.${key}`)),
          };
        },
        {}
      ),
    };
  }

  return {
    ...errorsSection1,
    ...errorsSection2,
  };
};

// TODO: Implement taxId validation for entity applications
const validateAccountInformation = (
  values: ApplicationModel,
  visibleSections: number | undefined,
  duplicateAccountCheck?: {
    isFetching: boolean;
    error?: boolean;
    errorMessage?: string[] | string | undefined;
  }
) => {
  let optionalSections = 0;
  // Section #1 (optional, visible for entity owner type)
  let errorsSection1 = {};

  // Comment this out due to possible old code for Entity Validation that is not needed
  // if (values.type === ApplicationType.Entity) {
  //   optionalSections++;
  //   errorsSection1 = {
  //     'entity.entityName': getRequiredError(values.entity && values.entity.entityName),
  //     'entity.entityTaxId': getRequiredError(values.entity && values.entity.entityTaxId),
  //     ...(values.entityAccountType === EntityAccountType.PersonalTrust
  //       ? {
  //           ...validateDate(values, 'entity.trustCreatedDate'),
  //         }
  //       : {
  //           'entity.entityYourTitle': getRequiredError(values.entity && values.entity.entityYourTitle),
  //           'entity.usStateOfRegistration':
  //             values.ownerCountry === Country.UnitedStatesOfAmerica
  //               ? getRequiredError(values.entity && values.entity.usStateOfRegistration)
  //               : undefined,
  //           'entity.businessNature': getRequiredError(values.entity && values.entity.businessNature),
  //         }),
  //     ...validateAddress(values, 'entity.address'),
  //     ...(values.entityAccountType !== EntityAccountType.PersonalTrust
  //       ? {
  //           'entity.businessPhone': getRequiredError(values.entity && values.entity.businessPhone),
  //         }
  //       : undefined),
  //   };
  // }

  // Section #2 (optional, visible for entity owner type AND non-trust entity types)
  let errorsSection2 = {};
  if (
    values.type === ApplicationType.Entity &&
    values.entityAccountType !== EntityAccountType.PersonalTrust &&
    (!visibleSections || visibleSections > 1)
  ) {
    optionalSections++;
    errorsSection2 = {
      'entity.officeLocations': getRequiredError(values.entity && values.entity.officeLocations),
      'entity.accountActivity': getRequiredError(values.entity && values.entity.accountActivity),
      'entity.initialDepositAmount': getRequiredError(values.entity && values.entity.initialDepositAmount),
      'entity.initialDepositMethod': getRequiredError(values.entity && values.entity.initialDepositMethod),
      'entity.primarySourceOfFunds': getRequiredError(values.entity && values.entity.primarySourceOfFunds),
      'entity.expectedWithdrawalFrequency': getRequiredError(
        values.entity && values.entity.expectedWithdrawalFrequency
      ),
      'entity.primaryBankName': getRequiredError(values.entity && values.entity.primaryBankName),
      ..._.reduce(
        values.entity && values.entity.tradingBlockAccounts,
        (acc: { [key: string]: string | undefined }, account, i) => {
          return {
            ...acc,
            [`entity.tradingBlockAccounts[${i}].accountNumber`]: getRequiredError(account.accountNumber),
            [`entity.tradingBlockAccounts[${i}].accountName`]: getRequiredError(account.accountName),
          };
        },
        {}
      ),
    };
  }

  const primaryAccountHolderPrefix = 'primaryAccountHolder';
  // Section #3
  let errorsSection3 = {};
  if (!visibleSections || visibleSections > optionalSections) {
    errorsSection3 = {
      ...validatePhones(values, primaryAccountHolderPrefix),
      ...validateAddress(values, `${primaryAccountHolderPrefix}.address`, true),
      ...(values.primaryAccountHolder && values.primaryAccountHolder.differentMailingAddress
        ? validateAddress(values, `${primaryAccountHolderPrefix}.mailingAddress`)
        : undefined),
    };
  }
  // Section #4
  let errorsSection4 = {};
  if (!visibleSections || visibleSections > 1 + optionalSections) {
    errorsSection4 = validateAccountHolder(
      values,
      values.primaryAccountHolder || DefaultAccountHolder,
      primaryAccountHolderPrefix,
      duplicateAccountCheck
    );
  }

  const jointAccountHolderPrefix = 'secondaryAccountHolder';
  // Section #5 (optional, visible for joint owner type)
  let errorsSection5 = {};
  if (
    (values.type === ApplicationType.Joint || values.type === ApplicationType.UGMA) &&
    (!visibleSections || visibleSections > 2 + optionalSections)
  ) {
    errorsSection5 = {
      ...validatePerson(values, jointAccountHolderPrefix, PersonType.AccountHolder),
      ...validatePhones(values, jointAccountHolderPrefix),
      ...validateAddress(values, `${jointAccountHolderPrefix}.address`, true),
      ...(values.secondaryAccountHolder && values.secondaryAccountHolder.differentMailingAddress
        ? validateAddress(values, `${jointAccountHolderPrefix}.mailingAddress`)
        : undefined),
    };
  }
  // Section #6 (optional, visible for joint owner type)
  let errorsSection6 = {};
  if (
    (values.type === ApplicationType.Joint || values.type === ApplicationType.UGMA) &&
    (!visibleSections || visibleSections > 3 + optionalSections)
  ) {
    errorsSection6 = validateAccountHolder(
      values,
      values.secondaryAccountHolder || DefaultAccountHolder,
      jointAccountHolderPrefix,
      duplicateAccountCheck
    );
  }

  return {
    ...errorsSection1,
    ...errorsSection2,
    ...errorsSection3,
    ...errorsSection4,
    ...errorsSection5,
    ...errorsSection6,
  };
};

const validateAccountHolder = (
  values: ApplicationModel,
  accountHolder: AccountHolderModel,
  accountHolderPrefix: string,
  duplicateAccountCheck?: {
    isFetching: boolean;
    error?: boolean;
    errorMessage?: string[] | string | undefined;
  }
) => {
  const errorMessage = (duplicateAccountCheck && duplicateAccountCheck.errorMessage) || '';
  const isForeign =
    accountHolder &&
    accountHolder.citizenshipCountry &&
    accountHolder.citizenshipCountry !== Country.UnitedStatesOfAmerica;
  // need to validate ssn only if it is not foreign
  return {
    ...validatePersonDetails(values, accountHolderPrefix, true, duplicateAccountCheck),
    ...(!values.entityAccountType
      ? {
          [`${accountHolderPrefix}.maritalStatus`]: getRequiredError(accountHolder.maritalStatus),
          [`${accountHolderPrefix}.dependents`]:
            getRequiredError(accountHolder.dependents) ||
            getInvalidError(
              _.isNumber(accountHolder.dependents) &&
                accountHolder.dependents > -1 &&
                _.parseInt(`${accountHolder.dependents}`) === _.toNumber(accountHolder.dependents)
            ),
        }
      : undefined),
    ...(accountHolder.citizenshipCountry !== Country.UnitedStatesOfAmerica
      ? {
          [`${accountHolderPrefix}.citizenshipCountry`]: getRequiredError(accountHolder.citizenshipCountry),
        }
      : undefined),
    ...(accountHolder.citizenshipCountry !== Country.UnitedStatesOfAmerica
      ? {
          [`${accountHolderPrefix}.birthCountry`]: getRequiredError(accountHolder.birthCountry),
        }
      : undefined),
    ...(accountHolder.citizenshipCountry !== Country.UnitedStatesOfAmerica &&
    accountHolder.address.country === Country.UnitedStatesOfAmerica &&
    !values.entityAccountType
      ? {
          [`${accountHolderPrefix}.visaType`]: getRequiredError(accountHolder.visaType),
          ...validateDate(values, `${accountHolderPrefix}.visaExpirationDate`),
        }
      : undefined),
    ...(values.type !== ApplicationType.Entity || values.entityAccountType === EntityAccountType.PersonalTrust
      ? {
          [`${accountHolderPrefix}.employmentType`]: getRequiredError(accountHolder.employmentType),
          ...(accountHolder.employmentType === EmploymentType.Employed
            ? {
                [`${accountHolderPrefix}.jobPosition`]: getRequiredError(accountHolder.jobPosition),
                [`${accountHolderPrefix}.yearsEmployed`]: getRequiredError(accountHolder.yearsEmployed),
                [`${accountHolderPrefix}.employer`]: getRequiredError(accountHolder.employer),
                ...validateAddress(values, `${accountHolderPrefix}.employerAddress`),
              }
            : undefined),
        }
      : undefined),
    // need to validate ssn only if it is not foreign
    ...(!isForeign
      ? {
          [`${accountHolderPrefix}.ssn`]:
            getRequiredError(accountHolder.ssn) ||
            getInvalidError(RE_SSN.test(accountHolder.ssn)) ||
            getDuplicateAccountError(
              duplicateAccountCheck && !duplicateAccountCheck.error && errorMessage !== '',
              errorMessage
            ),
        }
      : undefined),

    ...validateForeignDueDiligence(
      accountHolder,
      values.foreignDueDiligence || DefaultForeignDueDiligence,
      accountHolderPrefix
    ),
    ...validateForeignGovernmentId(accountHolder, accountHolderPrefix, values),
    ...validateForeignTaxWithholding(accountHolder, accountHolderPrefix, values),
  };
};

const validateForeignDueDiligence = (
  accountHolder: AccountHolderModel,
  foreignDueDiligence: ForeignDueDiligenceModel,
  accountHolderPrefix: string
) => {
  if (
    accountHolderPrefix != 'primaryAccountHolder' ||
    accountHolder.address.country === Country.UnitedStatesOfAmerica
  ) {
    return undefined;
  }

  return {
    [`foreignDueDiligence.initialDepositAmount`]: getRequiredError(foreignDueDiligence.initialDepositAmount),
    [`foreignDueDiligence.initialDepositType`]: getRequiredError(foreignDueDiligence.initialDepositType),
    [`foreignDueDiligence.expectedWithdrawalFrequency`]: getRequiredError(
      foreignDueDiligence.expectedWithdrawalFrequency
    ),
    [`foreignDueDiligence.primaryBanking[0]`]: getRequiredError(foreignDueDiligence.primaryBanking[0]),
    ...(foreignDueDiligence.referredToBroker === false
      ? {
          [`foreignDueDiligence.initialContact`]: getRequiredError(foreignDueDiligence.initialContact),
        }
      : {
          [`foreignDueDiligence.referredName`]: getRequiredError(foreignDueDiligence.referredName),
          [`foreignDueDiligence.referredRelationship`]: getRequiredError(foreignDueDiligence.referredRelationship),
        }),
  };
};

const validateForeignGovernmentId = (
  accountHolder: AccountHolderModel,
  accountHolderPrefix: string,
  values: ApplicationModel
) => {
  if (values.primaryAccountHolder.address.country === Country.UnitedStatesOfAmerica) {
    return undefined;
  }

  // if the account holder is not a US citizen, then the foreign government ID is required
  // two fields are present for the foreign government ID, the ID number and the issuing country
  return {
    [`${accountHolderPrefix}.governmentId.identificationNumber`]: getRequiredError(
      accountHolder.governmentId && accountHolder.governmentId.identificationNumber
    ),
    [`${accountHolderPrefix}.governmentId.countryOfIssue`]: getRequiredError(
      accountHolder.governmentId && accountHolder.governmentId.countryOfIssue
    ),
  };
};

const validateForeignTaxWithholding = (
  accountHolder: AccountHolderModel,
  accountHolderPrefix: string,
  values: ApplicationModel
) => {
  const { foreignTaxWithholding } = accountHolder;
  const { jurisdictionDoesNotIssueTIN, notIssuedForeignTIN } = foreignTaxWithholding;
  // RQD only requires that the tax identification number be provided
  // all other fields are specific to Apex accounts
  const isRQDCleared = values.clearer === 'RQD';
  const hasTIN = accountHolder.foreignTaxWithholding.taxIdentificationNumber;

  if (accountHolder.address.country === Country.UnitedStatesOfAmerica) {
    return undefined;
  }

  if (isRQDCleared) {
    return {
      [`${accountHolderPrefix}.foreignTaxWithholding.taxIdentificationNumber`]: getRequiredError(
        !hasTIN ? false : true
      ),
    };
  } else {
    if (
      !accountHolder.foreignTaxWithholding.taxIdentificationNumber &&
      accountHolder.mailingAddress &&
      accountHolder.mailingAddress.country === Country.UnitedStatesOfAmerica &&
      accountHolder.address.country !== Country.UnitedStatesOfAmerica
    ) {
      return {
        [`${accountHolderPrefix}.foreignTaxWithholding.taxIdentificationNumber`]: getRequiredError(
          !jurisdictionDoesNotIssueTIN && !notIssuedForeignTIN ? false : true
        ),
        [`${accountHolderPrefix}.foreignTaxWithholding.jurisdictionDoesNotIssueTIN`]: getRequiredError(
          accountHolder.foreignTaxWithholding.jurisdictionDoesNotIssueTIN === null ? false : true
        ),
        [`${accountHolderPrefix}.foreignTaxWithholding.notIssuedForeignTIN`]:
          getRequiredError(accountHolder.foreignTaxWithholding.notIssuedForeignTIN === null ? false : true) ||
          getInvalidError(
            !accountHolder.foreignTaxWithholding.taxIdentificationNumber &&
              (accountHolder.foreignTaxWithholding.notIssuedForeignTIN === BooleanToggleType.No &&
                accountHolder.foreignTaxWithholding.jurisdictionDoesNotIssueTIN === BooleanToggleType.No)
          )
            ? false
            : true,
        [`${accountHolderPrefix}.foreignTaxWithholding.letterOfExplanation`]: getRequiredError(
          accountHolder.foreignTaxWithholding.letterOfExplanation === null ||
            _.isEmpty(accountHolder.foreignTaxWithholding.letterOfExplanation)
            ? false
            : true
        ),
        [`${accountHolderPrefix}.foreignTaxWithholding.foreignTINNotRequiredExplanation`]: getRequiredError(
          accountHolder.foreignTaxWithholding.foreignTINNotRequiredExplanation === null ||
            _.isEmpty(accountHolder.foreignTaxWithholding.foreignTINNotRequiredExplanation)
            ? false
            : true
        ),
        [`${accountHolderPrefix}.foreignTaxWithholding.treatyCountry`]: getRequiredError(
          accountHolder.foreignTaxWithholding.treatyCountry === null ? false : true
        ),
      };
    }
    if (
      !accountHolder.foreignTaxWithholding.taxIdentificationNumber &&
      accountHolder.address.country !== Country.UnitedStatesOfAmerica
    ) {
      return {
        [`${accountHolderPrefix}.foreignTaxWithholding.taxIdentificationNumber`]: getRequiredError(
          !jurisdictionDoesNotIssueTIN && !notIssuedForeignTIN ? false : true
        ),
        [`${accountHolderPrefix}.foreignTaxWithholding.jurisdictionDoesNotIssueTIN`]:
          getRequiredError(accountHolder.foreignTaxWithholding.jurisdictionDoesNotIssueTIN === null ? false : true) ||
          getInvalidError(
            !accountHolder.foreignTaxWithholding.taxIdentificationNumber &&
              (accountHolder.foreignTaxWithholding.notIssuedForeignTIN === BooleanToggleType.No &&
                accountHolder.foreignTaxWithholding.jurisdictionDoesNotIssueTIN === BooleanToggleType.No)
          )
            ? false
            : true,
        [`${accountHolderPrefix}.foreignTaxWithholding.notIssuedForeignTIN`]:
          getRequiredError(accountHolder.foreignTaxWithholding.notIssuedForeignTIN === null ? false : true) ||
          getInvalidError(
            !accountHolder.foreignTaxWithholding.taxIdentificationNumber &&
              (accountHolder.foreignTaxWithholding.notIssuedForeignTIN === BooleanToggleType.No &&
                accountHolder.foreignTaxWithholding.jurisdictionDoesNotIssueTIN === BooleanToggleType.No)
          )
            ? false
            : true,
        [`${accountHolderPrefix}.foreignTaxWithholding.treatyCountry`]: getRequiredError(
          accountHolder.foreignTaxWithholding.treatyCountry === null ? false : true
        ),
        [`${accountHolderPrefix}.foreignTaxWithholding.foreignTINNotRequiredExplanation`]:
          getRequiredError(
            !jurisdictionDoesNotIssueTIN &&
              (accountHolder.foreignTaxWithholding.foreignTINNotRequiredExplanation === null ||
                _.isEmpty(accountHolder.foreignTaxWithholding.foreignTINNotRequiredExplanation))
              ? false
              : true
          ) ||
          getInvalidLengthError(
            jurisdictionDoesNotIssueTIN &&
              accountHolder.foreignTaxWithholding.foreignTINNotRequiredExplanation &&
              accountHolder.foreignTaxWithholding.foreignTINNotRequiredExplanation.length <= 255
              ? false
              : true
          ),
      };
    }

    if (
      accountHolder.mailingAddress &&
      accountHolder.mailingAddress.country === Country.UnitedStatesOfAmerica &&
      accountHolder.address.country !== Country.UnitedStatesOfAmerica
    ) {
      return {
        [`${accountHolderPrefix}.foreignTaxWithholding.letterOfExplanation`]: getRequiredError(
          accountHolder.foreignTaxWithholding.letterOfExplanation === null ? false : true
        ),
        [`${accountHolderPrefix}.foreignTaxWithholding.treatyCountry`]: getRequiredError(
          accountHolder.foreignTaxWithholding.treatyCountry === null ? false : true
        ),
      };
    }
    if (!accountHolder.foreignTaxWithholding.treatyCountry) {
      return {
        [`${accountHolderPrefix}.foreignTaxWithholding.treatyCountry`]: getRequiredError(
          accountHolder.foreignTaxWithholding.treatyCountry === null ? false : true
        ),
      };
    }
  }
};

const validateBeneficiaries = (
  values: ApplicationModel,
  duplicateAccountCheck?: {
    isFetching: boolean;
    error?: boolean;
    errorMessage?: string[] | string | undefined;
  }
) => {
  const showBeneficiaries = showBeneficiariesFields(values);
  const showOwnersOfficers = showOwnersOfficersFields(values);
  const showTrustedContact = showTrustedContactFields(values);
  const showTenantsInCommonInterestPercent = showTenantsInCommonInterestPercentFields(values);

  return {
    ...(showBeneficiaries ? validateBeneficiariesFields(values, duplicateAccountCheck) : {}),
    ...(showOwnersOfficers ? validateOwnersOfficersFields(values, duplicateAccountCheck) : {}),
    ...(showTrustedContact ? validateTrustedContactFields(values) : {}),
    ...(showTenantsInCommonInterestPercent ? validateTenantsInCommonPercent(values) : {}),
  };
};

const validateBeneficiariesFields = (
  values: ApplicationModel,
  duplicateAccountCheck?: {
    isFetching: boolean;
    error?: boolean;
    errorMessage?: string[] | string | undefined;
  }
) => {
  const percentageErrors = validateBeneficiaryPercentages(values.beneficiaries || []);

  const otherValidationErrors = _.reduce(
    values.beneficiaries,
    (acc, b, i) => ({
      ...acc,
      [`beneficiaries[${i}].beneficiaryAccountType`]: getRequiredError(b.beneficiaryAccountType),
      [`beneficiaries[${i}].beneficiaryType`]: getRequiredError(b.beneficiaryType),
      ...(b.beneficiaryType === BeneficiaryType.Individual
        ? {
            [`beneficiaries[${i}].relationship`]: getRequiredError(b.relationship),
            ...validatePersonDetails(values, `beneficiaries[${i}]`, true, duplicateAccountCheck),
          }
        : {
            [`beneficiaries[${i}].trustName`]: getRequiredError(b.trustName),
            [`beneficiaries[${i}].ein`]: getRequiredError(b.ein) || getInvalidError(b.ein && RE_EIN.test(b.ein)),
          }),
      ...validateAddress(values, `beneficiaries[${i}].address`),
      [`beneficiaries[${i}].ssn`]: getRequiredError(b.ssn) || getInvalidError(RE_SSN.test(b.ssn)),
    }),
    {}
  );

  const finalErrors = {
    ...percentageErrors,
    ...otherValidationErrors,
  };

  return finalErrors;
};

const validateOwnersOfficersFields = (
  values: ApplicationModel,
  duplicateAccountCheck?: {
    isFetching: boolean;
    error?: boolean;
    errorMessage?: string[] | string | undefined;
  }
) => {
  return {
    ..._.reduce(
      values.ownersOfficers,
      (acc, o, i) => ({
        ...acc,
        ...validatePersonDetails(values, `ownersOfficers[${i}]`, true, duplicateAccountCheck),
        ...validateAddress(values, `ownersOfficers[${i}].address`),
        [`ownersOfficers[${i}].isOwner`]: getRequiredError(o.isOwner || o.isOfficer),
      }),
      {}
    ),
  };
};

const validateTrustedContactFields = (values: ApplicationModel) => {
  if (values.addTrustedContact) {
    return {
      'trustedContact.firstName':
        getRequiredError(_.get(values, 'trustedContact.firstName')) ||
        getInvalidError(isValidPersonName(_.get(values, 'trustedContact.firstName'))) ||
        getInvalidLengthError(
          _.get(values, 'trustedContact.firstName') && _.get(values, 'trustedContact.firstName').length <= 20
        ),
      'trustedContact.lastName':
        getRequiredError(_.get(values, 'trustedContact.lastName')) ||
        getInvalidError(isValidPersonName(_.get(values, 'trustedContact.lastName'))) ||
        getInvalidLengthError(
          _.get(values, 'trustedContact.lastName') && _.get(values, 'trustedContact.lastName').length <= 20
        ),
      ...validateAddress(values, 'trustedContact.address'),
      'trustedContact.email': getEmailError(values.trustedContact && values.trustedContact.email),
      'trustedContact.phones[0].phoneNumber':
        getRequiredError(values.trustedContact && values.trustedContact.phones[0].phoneNumber) ||
        getInvalidError(values.trustedContact && validatePhoneNumber(values.trustedContact.phones[0].phoneNumber)),
    };
  }

  return {};
};

const validateTenantsInCommonPercent = (values: ApplicationModel) => {
  const primaryPercent = values.primaryAccountHolder.jointTenantsInCommonInterestPercent;
  const secondaryPercent = values.secondaryAccountHolder
    ? values.secondaryAccountHolder.jointTenantsInCommonInterestPercent
    : null;

  if (primaryPercent && secondaryPercent) {
    const totalPercent = primaryPercent + secondaryPercent;

    return {
      [`primaryAccountHolder.jointTenantsInCommonInterestPercent`]:
        getRequiredError(primaryPercent === null ? false : true) || getAmountError(totalPercent === 100),
      [`secondaryAccountHolder.jointTenantsInCommonInterestPercent`]:
        getRequiredError(secondaryPercent === null ? false : true) || getAmountError(totalPercent === 100),
    };
  } else {
    return {
      [`primaryAccountHolder.jointTenantsInCommonInterestPercent`]: getRequiredError(
        primaryPercent === null ? false : true
      ),
      [`secondaryAccountHolder.jointTenantsInCommonInterestPercent`]: getRequiredError(
        primaryPercent === null ? false : true
      ),
    };
  }
};

function removeUndefinedKeys(obj: any) {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  // Recursively process each property of the object
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      obj[key] = removeUndefinedKeys(obj[key]);

      // Remove properties with undefined values
      if (obj[key] === undefined) {
        delete obj[key];
      }
    }
  }
  if (_.isEmpty(obj)) {
    return undefined;
  }
  return obj;
}

const validateTradingAuthorization = (values: ApplicationModel) => {
  if (values.tradingAuthorization && values.tradeAuthorization === BooleanToggleType.Yes) {
    const disclosures = values.tradingAuthorization.disclosures;
    const industryEmployed = disclosures && disclosures.industryEmployed;
    const stakeholder = disclosures && disclosures.stakeholder;
    const type = values.tradingAuthorization.type;
    const signedName = values.tradingAuthorization.signedName;
    const { firstName, lastName } = values.tradingAuthorization;
    const nameMatch = firstName && lastName && signedName && signedName === `${firstName} ${lastName}`;

    const isForeign =
      values.tradingAuthorization &&
      values.tradingAuthorization.citizenshipCountry &&
      values.tradingAuthorization.citizenshipCountry !== Country.UnitedStatesOfAmerica;

    const errors = {
      [`tradingAuthorization.type`]: getRequiredError(values.tradingAuthorization.type),
      ...validatePhone(
        _.first(values.tradingAuthorization && values.tradingAuthorization.phones),
        'tradingAuthorization.phones[0]'
      ),
      [`tradingAuthorization.email`]: getEmailError(values.tradingAuthorization && values.tradingAuthorization.email),

      ...validatePerson(values, 'tradingAuthorization', PersonType.TradeAuthorization),
      [`tradingAuthorization.disclosures.industryEmployed`]: getRequiredError(
        type === TradeAuthorizationType.Full ? industryEmployed : true
      ),
      [`tradingAuthorization.disclosures.stakeholder`]: getRequiredError(
        type === TradeAuthorizationType.Full ? stakeholder : true
      ),
      [`tradingAuthorization.disclosuresIndustryEmployedFirmName`]: getRequiredError(
        industryEmployed === BooleanToggleType.Yes
          ? values.tradingAuthorization.disclosuresIndustryEmployedFirmName
          : true
      ),
      [`tradingAuthorization.disclosuresStakeholderTickerSymbol`]: getRequiredError(
        stakeholder === BooleanToggleType.Yes ? values.tradingAuthorization.disclosuresStakeholderTickerSymbol : true
      ),
      [`tradingAuthorization.signedName`]: getRequiredError(signedName) || getMismatchError(nameMatch),
      [`tradingAuthorization.ssnSecret`]:
        (!isForeign &&
          (getRequiredError(_.get(values, `tradingAuthorization.ssnSecret`)) ||
            getInvalidError(RE_SSN.test(_.get(values, `tradingAuthorization.ssnSecret`))))) ||
        undefined,
      ['tradingAuthorization.employmentType']: getRequiredError(values.tradingAuthorization.employmentType),
      [`tradingAuthorization.employer`]:
        // check if employment type is employed, if so check if employer is populated
        // if employer is populated check that it is below 60 characters and return error if not
        (values.tradingAuthorization.employmentType === EmploymentType.Employed &&
          getRequiredError(values.tradingAuthorization.employer)) ||
        (values.tradingAuthorization.employmentType === EmploymentType.Employed &&
          getInvalidLengthError(
            values.tradingAuthorization.employer && values.tradingAuthorization.employer.length <= 60
          )),
      [`tradingAuthorization.jobPosition`]:
        values.tradingAuthorization.employmentType === EmploymentType.Employed &&
        getRequiredError(values.tradingAuthorization.jobPosition),
      ['tradingAuthorization.dateOfBirth']: { ...validateDate(values, 'tradingAuthorization.dateOfBirth', true) },
    };

    const filteredErrors = removeUndefinedKeys(errors);
    return filteredErrors;
  } else {
    return {};
  }
};

const validateAgreements = (values: ApplicationModel, visibleSections: number | undefined) => {
  let optionalSections = 0;

  // Section #1
  const errorsSection1 = {
    ...(!isForeignAccount(values.primaryAccountHolder.address.country) && {
      tradeAuthorization: getRequiredError(values.tradeAuthorization),
    }),
    ...validateTradingAuthorization(values),
    ...validateAccountHolderDisclosures(values, 'primaryAccountHolder'),
  };

  // Section #2 (optional, visible for joint owner type)
  let errorsSection2 = {};
  if (values.type === ApplicationType.Joint && (!visibleSections || visibleSections > 1)) {
    optionalSections++;
    errorsSection2 = validateAccountHolderDisclosures(values, 'secondaryAccountHolder');
  }

  // Section #3
  let errorsSection3 = {};
  if (!visibleSections || visibleSections > 1 + optionalSections) {
    errorsSection3 = {
      ...validateAccountDisclosures(values),
      ...validateServiceProfile(values),
      ...validateMarketDataQuestionnaire(values),
      disclosuresForeignBankUSAgent: getRequiredError(
        values.accountDisclosures && values.accountDisclosures.foreignBank === BooleanToggleType.Yes
          ? values.disclosuresForeignBankUSAgent
          : true
      ),
    };
    if (values.type === ApplicationType.Entity) {
      errorsSection3 = {
        ...errorsSection3,
        accountDisclosures:
          _.get(errorsSection3, 'accountDisclosures') ||
          getRequiredError(
            _.every(
              AllEntityAccountDisclosureKeys,
              (d: AccountDisclosureKey) => values.accountDisclosures && !!values.accountDisclosures[d]
            )
          ),
      };

      if (values.entityAccountType !== EntityAccountType.PersonalTrust) {
        errorsSection3 = {
          ...errorsSection3,
          accountDisclosures:
            _.get(errorsSection3, 'accountDisclosures') ||
            getRequiredError(
              _.every(
                AllEntityNonTrustAccountDisclosureKeys,
                (d: AccountDisclosureKey) => values.accountDisclosures && !!values.accountDisclosures[d]
              )
            ),
          disclosuresEntityNegativeNewsInfo: getRequiredError(
            values.accountDisclosures && values.accountDisclosures.entityNegativeNews === BooleanToggleType.Yes
              ? values.disclosuresEntityNegativeNewsInfo
              : true
          ),
          ...(values.accountDisclosures && values.accountDisclosures.entityPublicOfficials === BooleanToggleType.Yes
            ? validatePublicOfficialsFields(values)
            : {}),
        };
      }
    }
  }

  // Section #4
  let errorsSection4 = {};
  if (!visibleSections || visibleSections > 2 + optionalSections) {
    errorsSection4 = {
      agreeToElectronicConsent: getRequiredError(values.agreeToElectronicConsent),
      ...validateAccountAgreements(values),
    };
  }

  return {
    ...errorsSection1,
    ...errorsSection2,
    ...errorsSection3,
    ...errorsSection4,
  };
};

const validateAccountHolderDisclosures = (values: ApplicationModel, accountHolderPrefix: AccountHolderField) => {
  const accountHolder = values[accountHolderPrefix];
  const disclosures = accountHolder && accountHolder.disclosures;
  const accountDisclosures = values.accountDisclosures;
  return {
    [`${accountHolderPrefix}.disclosures`]: getRequiredError(
      _.every(AllAccountHolderDisclosureKeys, (d: AccountHolderDisclosureKey) => disclosures && !!disclosures[d])
    ),
    [`${accountHolderPrefix}.disclosures.stakeholder`]: getRequiredError(
      disclosures && disclosures.stakeholder === null ? false : true
    ),
    [`${accountHolderPrefix}.disclosures.govOfficial`]: getRequiredError(
      disclosures && disclosures.govOfficial === null ? false : true
    ),
    [`${accountHolderPrefix}.disclosures.irsWithholding`]: getRequiredError(
      disclosures && disclosures.irsWithholding === null ? false : true
    ),
    [`${accountHolderPrefix}.disclosures.industryEmployed`]: getRequiredError(
      disclosures && disclosures.industryEmployed === null ? false : true
    ),

    [`${accountHolderPrefix}.disclosuresStakeholderTickerSymbol`]: getRequiredError(
      disclosures && disclosures.stakeholder === BooleanToggleType.Yes
        ? accountHolder && accountHolder.disclosuresStakeholderTickerSymbol
        : true
    ),
    [`${accountHolderPrefix}.disclosuresIndustryEmployedFirmName`]: getRequiredError(
      disclosures && disclosures.industryEmployed === BooleanToggleType.Yes
        ? accountHolder && accountHolder.disclosuresIndustryEmployedFirmName
        : true
    ),
    [`${accountHolderPrefix}.disclosuresGovOfficialImmediateFamily`]: getRequiredError(
      disclosures && disclosures.govOfficial === BooleanToggleType.Yes
        ? accountHolder && accountHolder.disclosuresGovOfficialImmediateFamily
        : true
    ),
    [`${accountHolderPrefix}.disclosuresGovOfficialPoliticalOrg`]: getRequiredError(
      disclosures && disclosures.govOfficial === BooleanToggleType.Yes
        ? accountHolder && accountHolder.disclosuresGovOfficialPoliticalOrg
        : true
    ),
  };
};

const validateAccountDisclosures = (values: ApplicationModel) => {
  const accountDisclosures = values.accountDisclosures;

  return {
    [`accountDisclosures.foreignBank`]: getRequiredError(
      accountDisclosures && accountDisclosures.foreignBank === null ? false : true
    ),
    [`accountDisclosures.foreignShellBank`]: getRequiredError(
      accountDisclosures && accountDisclosures.foreignShellBank === null ? false : true
    ),
    [`accountDisclosures.foreignFinancialInstitution`]: getRequiredError(
      accountDisclosures && accountDisclosures.foreignFinancialInstitution === null ? false : true
    ),
    [`accountDisclosures.proprietarySecuritiesAccount`]: getRequiredError(
      accountDisclosures && accountDisclosures.proprietarySecuritiesAccount === null ? false : true
    ),
  };
};

export const isProCheck = (values: ApplicationModel) => {
  const { primaryAccountHolder, accountDisclosures } = values;
  const { disclosures } = primaryAccountHolder;
  const { marketData } = values;

  if (
    disclosures.industryEmployed === BooleanToggleType.Yes ||
    disclosures.stakeholder === BooleanToggleType.Yes ||
    disclosures.govOfficial === BooleanToggleType.Yes ||
    (accountDisclosures && accountDisclosures.foreignShellBank === BooleanToggleType.Yes) ||
    marketData.personalUse === BooleanToggleType.No ||
    marketData.associationRegistration === BooleanToggleType.Yes ||
    marketData.benefitsReceived === BooleanToggleType.Yes ||
    marketData.capitalTrading === BooleanToggleType.Yes ||
    marketData.corporationTrading === BooleanToggleType.Yes ||
    marketData.investmentProfessional === BooleanToggleType.Yes ||
    marketData.secCftcRegistration === BooleanToggleType.Yes ||
    marketData.jobFunctions === BooleanToggleType.Yes
  ) {
    return true;
  } else {
    return false;
  }
};

const validateMarketDataQuestionnaire = (values: ApplicationModel) => {
  const marketData = values.marketData;
  const marketDataType = values.marketDataType;
  const isPro = isProCheck(values);
  const {
    associationRegistration,
    benefitsReceived,
    capitalTrading,
    investmentProfessional,
    jobFunctions,
    personalUse,
    secCftcRegistration,
  } = marketData;

  if (!personalUse) {
    return {
      ['marketData.personalUse']: getRequiredError(personalUse),
      ...(isPro && { marketDataType: getRequiredError(marketDataType) }),
    };
  }

  if (
    personalUse === BooleanToggleType.No ||
    (personalUse === BooleanToggleType.Yes &&
      associationRegistration === BooleanToggleType.No &&
      secCftcRegistration === BooleanToggleType.No &&
      jobFunctions === BooleanToggleType.No &&
      isPro)
  ) {
    return { marketDataType: getRequiredError(marketDataType) };
  }

  if (
    personalUse === BooleanToggleType.Yes &&
    (associationRegistration === BooleanToggleType.Yes ||
      secCftcRegistration === BooleanToggleType.Yes ||
      jobFunctions === BooleanToggleType.Yes) &&
    investmentProfessional === BooleanToggleType.Yes &&
    capitalTrading === BooleanToggleType.Yes
  ) {
    return {
      ['marketData.associationRegistration']: getRequiredError(associationRegistration),
      ['marketData.secCftcRegistration']: getRequiredError(secCftcRegistration),
      ['marketData.jobFunctions']: getRequiredError(jobFunctions),
      ['marketData.investmentProfessional']: getRequiredError(investmentProfessional),
      ['marketData.capitalTrading']: getRequiredError(capitalTrading),
      ['marketData.benefitsReceived']: getRequiredError(benefitsReceived),
      ...(isPro && { marketDataType: getRequiredError(marketDataType) }),
    };
  }

  if (
    personalUse === BooleanToggleType.Yes &&
    (associationRegistration === BooleanToggleType.Yes ||
      secCftcRegistration === BooleanToggleType.Yes ||
      jobFunctions === BooleanToggleType.Yes) &&
    investmentProfessional === BooleanToggleType.Yes
  ) {
    return {
      ['marketData.associationRegistration']: getRequiredError(associationRegistration),
      ['marketData.secCftcRegistration']: getRequiredError(secCftcRegistration),
      ['marketData.jobFunctions']: getRequiredError(jobFunctions),
      ['marketData.investmentProfessional']: getRequiredError(investmentProfessional),
      ['marketData.capitalTrading']: getRequiredError(capitalTrading),
      ...(isPro && { marketDataType: getRequiredError(marketDataType) }),
    };
  }

  if (
    personalUse === BooleanToggleType.Yes &&
    (associationRegistration === BooleanToggleType.Yes ||
      secCftcRegistration === BooleanToggleType.Yes ||
      jobFunctions === BooleanToggleType.Yes)
  ) {
    return {
      ['marketData.associationRegistration']: getRequiredError(associationRegistration),
      ['marketData.secCftcRegistration']: getRequiredError(secCftcRegistration),
      ['marketData.jobFunctions']: getRequiredError(jobFunctions),
      ['marketData.investmentProfessional']: getRequiredError(investmentProfessional),
      ...(isPro && { marketDataType: getRequiredError(marketDataType) }),
    };
  }

  if (personalUse === BooleanToggleType.Yes && (!associationRegistration || !secCftcRegistration || !jobFunctions)) {
    return {
      ['marketData.associationRegistration']: getRequiredError(associationRegistration),
      ['marketData.secCftcRegistration']: getRequiredError(secCftcRegistration),
      ['marketData.jobFunctions']: getRequiredError(jobFunctions),
      ...(isPro && { marketDataType: getRequiredError(marketDataType) }),
    };
  }
};

const validateServiceProfile = (values: ApplicationModel) => {
  const serviceProfile = values.serviceProfile;

  return {
    [`serviceProfile.issuerDirectCommunication`]: getRequiredError(
      serviceProfile && serviceProfile.issuerDirectCommunication === null ? false : true
    ),
  };
};

const validateAccountAgreements = (values: ApplicationModel) => {
  const agreementValues = values.agreements;
  const agreeToElectronicConsent = values.agreeToElectronicConsent;
  const accountAgreementKeys = getAllAgreementKeysForApplication(values);
  let errorAgreements = [];
  let errors: { [key: string]: any } = {};

  if (agreeToElectronicConsent) {
    for (let index = 0; index < accountAgreementKeys.length; index++) {
      const agreementKey = accountAgreementKeys[index];
      const value = _.get(agreementValues, agreementKey);
      // Check for null, undefined, or 'no' - all indicate the agreement is not accepted
      if (value === null || value === undefined || value === 'no') {
        errorAgreements.push(agreementKey);
      }
    }

    for (let index = 0; index < errorAgreements.length; index++) {
      const error = errorAgreements[index];
      errors[error] = 'error';
    }

    const agreements = {
      agreements: errors,
    };

    if (_.isEmpty(errors)) {
      return null;
    }
    return agreements;
  } else {
    return null;
  }
};

const validatePublicOfficialsFields = (values: ApplicationModel) => {
  return {
    ..._.reduce(
      values.disclosuresEntityPublicOfficials,
      (acc, p, i) => ({
        ...acc,
        [`disclosuresEntityPublicOfficials[${i}].name`]: getRequiredError(p.name),
        [`disclosuresEntityPublicOfficials[${i}].role`]: getRequiredError(p.role),
        [`disclosuresEntityPublicOfficials[${i}].title`]: getRequiredError(p.title),
        [`disclosuresEntityPublicOfficials[${i}].organization`]: getRequiredError(p.organization),
        [`disclosuresEntityPublicOfficials[${i}].familyNames`]: getRequiredError(p.familyNames),
      }),
      {}
    ),
  };
};

const validateSign = (values: ApplicationModel) => {
  const ownerNames = getOwnerNamesForSignStep(values);
  return {
    ..._.reduce(
      ownerNames,
      (acc: { [key: string]: string | undefined }, name, i) => {
        return {
          ...acc,
          [`signedNames[${i}]`]:
            getRequiredError(values.signedNames && values.signedNames[i]) ||
            getInvalidError(values.signedNames && values.signedNames[i] && values.signedNames[i] === name),
        };
      },
      {}
    ),
    'entity.secretaryName':
      values.type === ApplicationType.Entity &&
      _.includes([EntityAccountType.CCorporation, EntityAccountType.SCorporation], values.entityAccountType)
        ? getRequiredError(values.entity && values.entity.secretaryName)
        : undefined,
  };
};

const validateDate = (values: ApplicationModel, key: string, testOver18 = false) => {
  const prefix = `${key}.`;
  const date = dayjs(
    `${_.get(values, `${prefix}month`)}/${_.get(values, `${prefix}day`)}/${_.get(values, `${prefix}year`)}`
  );

  return {
    [`${prefix}month`]: getRequiredError(_.get(values, `${prefix}month`)),
    [`${prefix}day`]: getRequiredError(_.get(values, `${prefix}day`)) || getInvalidDateError(_.get(values, key)),
    [`${prefix}year`]:
      getRequiredError(_.get(values, `${prefix}year`)) ||
      (testOver18 && getInvalidError(date.isValid() && date.isBefore(dayjs().subtract(18, 'year')))),
  };
};

// USPS Address Verification using new backend REST endpoint
const uspsVerification = async (values: ApplicationModel, field?: string, apiToken?: string) => {
  const prefix = `${field || 'address'}.`;
  const tradingApiUrl = config.tradingApi;

  // User Input - Address Values
  let address = {
    address1: _.get(values, `${prefix}address1`),
    address2: _.get(values, `${prefix}address2`),
    city: _.get(values, `${prefix}city`),
    state: _.get(values, `${prefix}state`),
    postalCode: _.get(values, `${prefix}postalCode`),
    country: _.get(values, `${prefix}country`),
  };

  const { address1, address2, city, state, postalCode, country } = address;

  // If address has been provided, excluding postal code, and country is US, then perform USPS verification
  try {
    if (address1 && city && state && country === Country.UnitedStatesOfAmerica && state !== StateEnum.PuertoRico) {
      // Get API token from store if not provided
      let token = apiToken;
      if (!token) {
        try {
          const state = DataStore.getState();
          token = state.ui.apiToken;
        } catch (err) {
          console.warn('Could not access store to get API token for USPS verification:', err);
        }
      }

      // Don't make API call without authentication token
      if (!token) {
        console.warn('USPS verification skipped: API token not available');
        return;
      }

      // Build query parameters with %20 encoding for spaces
      const queryParams: string[] = [];
      queryParams.push(`address1=${encodeURIComponent((address1 || '').trim())}`);
      queryParams.push(`city=${encodeURIComponent((city || '').trim())}`);
      queryParams.push(`state=${encodeURIComponent((state || '').trim())}`);

      // Add address2 if provided
      if (address2) {
        queryParams.push(`address2=${encodeURIComponent(address2.trim())}`);
      }

      const queryString = queryParams.join('&');

      // Build request headers
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: token, // Always include token - we've verified it exists above
      };

      const url = `${tradingApiUrl}/USPSAddress?${queryString}`;

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        // Handle HTTP errors - use layman-friendly message for users
        return {
          error: {
            number: response.status.toString(),
            source: 'HTTP',
            description: "We couldn't verify this address. Please check your information and try again.",
          },
        };
      }

      let jsonResult: any;
      try {
        const responseText = await response.text();
        jsonResult = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse USPS response as JSON:', parseError);
        return {
          error: {
            number: '0',
            source: 'Parse',
            description: "We couldn't verify this address. Please try again.",
          },
        };
      }

      // Check ResponseCode (0 = success, non-zero = error)
      if (jsonResult.ResponseCode !== 0) {
        // Handle different error response formats - use layman-friendly messages
        let errorDescription = "We couldn't verify this address. Please check your information and try again.";
        if (jsonResult.Payload) {
          if (jsonResult.Payload.error) {
            errorDescription = jsonResult.Payload.error;
          } else if (typeof jsonResult.Payload === 'string') {
            errorDescription = jsonResult.Payload;
          } else if (jsonResult.Payload.description) {
            errorDescription = jsonResult.Payload.description;
          } else if (jsonResult.Payload.message) {
            errorDescription = jsonResult.Payload.message;
          } else {
            // Fallback: stringify if it's an object
            errorDescription = JSON.stringify(jsonResult.Payload);
          }
        } else if (jsonResult.error) {
          errorDescription =
            typeof jsonResult.error === 'string'
              ? jsonResult.error
              : jsonResult.error.description || 'Address validation failed';
        } else if (jsonResult.message) {
          errorDescription = jsonResult.message;
        } else {
          errorDescription = "We couldn't verify this address. Please check your information and try again.";
        }

        console.error('USPS validation error:', {
          ResponseCode: jsonResult.ResponseCode,
          Payload: jsonResult.Payload,
          FullResponse: jsonResult,
          HasAuthToken: !!apiToken,
        });

        return {
          error: {
            number: (jsonResult.ResponseCode && jsonResult.ResponseCode.toString()) || '0',
            source: 'USPS',
            description: errorDescription,
          },
        };
      }

      const payload = jsonResult.Payload;
      if (!payload) {
        return {
          error: {
            number: '0',
            source: 'USPS',
            description: "We couldn't verify this address. Please try again.",
          },
        };
      }

      // Split postalCode into zip5 and zip4
      let zip5 = '';
      let zip4 = '';
      if (payload.postalCode) {
        const zipParts = payload.postalCode.split('-');
        zip5 = zipParts[0] || '';
        zip4 = zipParts[1] || '';
      }

      // Return the standardized address components
      // Note: New API doesn't return address2 separately, so we set it to null
      return {
        city: payload.city || '',
        state: payload.state || '',
        zip5: zip5,
        zip4: zip4,
        uspsAddress1: payload.address1 || '',
        uspsAddress2: null, // New API doesn't provide address2 separately
        isPoBox: payload.isPoBox || false,
        isSecondaryInformationRequired: payload.isSecondaryInformationRequired || false,
        isSecondaryInformationNotRecognized: payload.isSecondaryInformationNotRecognized || false,
      };
    }
  } catch (err) {
    console.error('USPS verification error:', err);
    return {
      error: {
        number: '0',
        source: 'Network',
        description: "We couldn't verify this address. Please check your connection and try again.",
      },
    };
  }
};

// Function to get USPS response from storage
const getUspsResponseFromStorage = (prefix: any) => {
  const uspsResponseFromStorage = sessionStorage.getItem(`${prefix}uspsResponse`);
  return uspsResponseFromStorage && uspsResponseFromStorage !== 'undefined' ? uspsResponseFromStorage : null;
};

// Async function to handle USPS verification
const getUSPS = async (values: ApplicationModel, field: string | undefined, apiToken?: string) => {
  await new Promise(resolve => setTimeout(resolve, 100));
  return await uspsVerification(values, field, apiToken);
};

// Create a unique key for an address to use for caching
const createAddressKey = (address: {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  country?: string;
}): string => {
  const normalized = {
    address1: (address.address1 || '').trim().toUpperCase(),
    address2: (address.address2 || '').trim().toUpperCase(),
    city: (address.city || '').trim().toUpperCase(),
    state: (address.state || '').trim().toUpperCase(),
    country: (address.country || '').trim().toUpperCase(),
  };
  return `${normalized.address1}|${normalized.address2}|${normalized.city}|${normalized.state}|${normalized.country}`;
};

// In-memory cache for pending requests to prevent duplicate concurrent calls
const pendingRequests = new Map<string, Promise<any>>();

// Debounce timers map - keyed by address key
const debounceTimers = new Map<string, NodeJS.Timeout>();

// Debounce delay in milliseconds
const DEBOUNCE_DELAY = 500;

// Exported function to trigger USPS verification independently
// This can be called from components with debounced validation
export const triggerUspsVerification = async (
  address: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  },
  prefix: string,
  apiToken?: string
): Promise<any> => {
  const { address1, address2, city, state, postalCode, country } = address;

  // Check if validation should run (US country, required fields present, not Puerto Rico)
  if (!address1 || !city || !state || country !== Country.UnitedStatesOfAmerica || state === StateEnum.PuertoRico) {
    // Clear USPS storage if fields are cleared
    if (!address1 || !city) {
      sessionStorage.setItem(`${prefix}uspsResponse`, 'undefined');
    }
    // Clear any pending debounce timer
    const addressKey = createAddressKey({ address1, address2, city, state, country });
    const existingTimer = debounceTimers.get(addressKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
      debounceTimers.delete(addressKey);
    }
    return null;
  }

  // Get API token from store if not provided
  let token = apiToken;
  if (!token) {
    try {
      const state = DataStore.getState();
      token = state.ui.apiToken;
    } catch (err) {
      console.warn('Could not access store to get API token for USPS verification:', err);
    }
  }

  // Don't make API call without authentication token
  if (!token) {
    console.warn('USPS verification skipped: API token not available');
    return null;
  }

  // Create address key for caching
  const addressKey = createAddressKey({ address1, address2, city, state, country });

  // Check if we already have a cached result for this exact address
  const storageKey = `${prefix}uspsResponse`;
  const cachedResponse = sessionStorage.getItem(storageKey);

  if (cachedResponse && cachedResponse !== 'undefined') {
    try {
      const cached = JSON.parse(cachedResponse);
      // Check if cached result includes the address key or matches the current address
      if (
        cached.addressKey === addressKey ||
        (cached.uspsAddress1 &&
          cached.uspsAddress1.toUpperCase() === (address1 || '').trim().toUpperCase() &&
          cached.city &&
          cached.city.toUpperCase() === (city || '').trim().toUpperCase() &&
          cached.state &&
          cached.state.toUpperCase() === (state || '').trim().toUpperCase())
      ) {
        // We have a valid cached result for this address - return it without making API call
        // Clear any pending debounce timer since we have a cached result
        const existingTimer = debounceTimers.get(addressKey);
        if (existingTimer) {
          clearTimeout(existingTimer);
          debounceTimers.delete(addressKey);
        }
        return cached;
      }
    } catch (e) {
      // Invalid cached data, continue to make API call
    }
  }

  // Check if there's already a pending request for this exact address
  if (pendingRequests.has(addressKey)) {
    // Return the existing promise to avoid duplicate requests
    return pendingRequests.get(addressKey);
  }

  // Clear any existing debounce timer for this address
  const existingTimer = debounceTimers.get(addressKey);
  if (existingTimer) {
    clearTimeout(existingTimer);
  }

  // Create a promise that will resolve after debounce delay
  const debouncedPromise = new Promise<any>(resolve => {
    const timer = setTimeout(async () => {
      // Remove timer from map
      debounceTimers.delete(addressKey);

      // Now actually make the API call
      const requestPromise = (async () => {
        try {
          const tradingApiUrl = config.tradingApi;

          // Build query parameters with %20 encoding for spaces
          const queryParams: string[] = [];
          queryParams.push(`address1=${encodeURIComponent((address1 || '').trim())}`);
          queryParams.push(`city=${encodeURIComponent((city || '').trim())}`);
          queryParams.push(`state=${encodeURIComponent((state || '').trim())}`);

          // Add address2 if provided
          if (address2) {
            queryParams.push(`address2=${encodeURIComponent(address2.trim())}`);
          }

          const queryString = queryParams.join('&');

          // Build request headers - always include token (we've verified it exists above)
          const headers: HeadersInit = {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: token,
          };

          const response = await fetch(`${tradingApiUrl}/USPSAddress?${queryString}`, {
            method: 'GET',
            headers,
          });

          if (!response.ok) {
            // Handle HTTP errors - use layman-friendly message for users
            const result = {
              error: {
                number: response.status.toString(),
                source: 'HTTP',
                description: "We couldn't verify this address. Please check your information and try again.",
              },
            };
            sessionStorage.setItem(storageKey, JSON.stringify(result));
            return result;
          }

          const jsonResult: any = await response.json();

          // Check ResponseCode (0 = success, non-zero = error)
          if (jsonResult.ResponseCode !== 0) {
            // Handle different error response formats - use layman-friendly messages
            let errorDescription = "We couldn't verify this address. Please check your information and try again.";
            if (jsonResult.Payload) {
              if (jsonResult.Payload.error) {
                errorDescription = jsonResult.Payload.error;
              } else if (typeof jsonResult.Payload === 'string') {
                errorDescription = jsonResult.Payload;
              } else if (jsonResult.Payload.description) {
                errorDescription = jsonResult.Payload.description;
              } else if (jsonResult.Payload.message) {
                errorDescription = jsonResult.Payload.message;
              }
            } else if (jsonResult.error) {
              errorDescription =
                typeof jsonResult.error === 'string'
                  ? jsonResult.error
                  : jsonResult.error.description || 'Address validation failed';
            } else if (jsonResult.message) {
              errorDescription = jsonResult.message;
            }
            const result = {
              error: {
                number: (jsonResult.ResponseCode && jsonResult.ResponseCode.toString()) || '0',
                source: 'USPS',
                description: errorDescription,
              },
            };
            sessionStorage.setItem(storageKey, JSON.stringify(result));
            return result;
          }

          const payload = jsonResult.Payload;
          if (!payload) {
            const result = {
              error: {
                number: '0',
                source: 'USPS',
                description: "We couldn't verify this address. Please try again.",
              },
            };
            sessionStorage.setItem(storageKey, JSON.stringify(result));
            return result;
          }

          // Split postalCode into zip5 and zip4
          let zip5 = '';
          let zip4 = '';
          if (payload.postalCode) {
            const zipParts = payload.postalCode.split('-');
            zip5 = zipParts[0] || '';
            zip4 = zipParts[1] || '';
          }

          // Return the standardized address components
          // Note: New API doesn't return address2 separately, so we set it to null
          const result = {
            city: payload.city || '',
            state: payload.state || '',
            zip5: zip5,
            zip4: zip4,
            uspsAddress1: payload.address1 || '',
            uspsAddress2: null, // New API doesn't provide address2 separately
            isPoBox: payload.isPoBox || false,
            isSecondaryInformationRequired: payload.isSecondaryInformationRequired || false,
            isSecondaryInformationNotRecognized: payload.isSecondaryInformationNotRecognized || false,
            addressKey, // Store the address key for future cache lookups
          };
          // Store result in sessionStorage
          sessionStorage.setItem(storageKey, JSON.stringify(result));
          return result;
        } catch (err) {
          console.error('USPS verification error:', err);
          const result = {
            error: {
              number: '0',
              source: 'Network',
              description: err instanceof Error ? err.message : 'Network error occurred',
            },
          };
          sessionStorage.setItem(storageKey, JSON.stringify(result));
          return result;
        } finally {
          // Remove from pending requests after completion
          pendingRequests.delete(addressKey);
        }
      })();

      // Store the pending request
      pendingRequests.set(addressKey, requestPromise);

      // Resolve with the request promise
      resolve(requestPromise);
    }, DEBOUNCE_DELAY);

    // Store the debounce timer
    debounceTimers.set(addressKey, timer);
  });

  return debouncedPromise;
};

// Function to validate the address using the USPS response
const validateWithUspsResponse = (
  uspsResponse: any,
  address: any,
  prefix: any,
  values: ApplicationModel,
  field: string,
  apiToken?: string
) => {
  const isTrustedContactAddress = field && field.toLowerCase().includes('trustedcontact');
  let isValidAddress = false;
  let uspsResponseValue;

  // Destructuring address object to get individual address components
  const { address1, address2, city, state, country } = address;

  // Exclude Puerto Rico
  if (state === StateEnum.PuertoRico) {
    return {};
  }

  // Check if a USPS response exists and is not 'undefined'
  if (uspsResponse && uspsResponse !== 'undefined') {
    // Parse the USPS response
    uspsResponseValue = JSON.parse(uspsResponse);

    // Extract relevant data from the parsed USPS response
    const {
      city,
      state,
      zip5,
      error,
      uspsAddress1,
      uspsAddress2,
      isPoBox,
      isSecondaryInformationRequired,
      isSecondaryInformationNotRecognized,
    } = uspsResponseValue;

    // Check PO Box restriction (unless it's a trusted contact address)
    if (isPoBox && !isTrustedContactAddress) {
      isValidAddress = false; // PO Box addresses are not valid for non-trusted contact addresses
    }

    // Format the city name for comparison
    const formattedCityCapitalized = _.capitalize(city);
    const startCaseCity = _.startCase(formattedCityCapitalized);

    // Compare each component of the address with the USPS response
    const isCityEqual = _.toLower(startCaseCity) === _.toLower(_.get(values, `${prefix}city`));
    // Extract zip5 from postalCode (handle zip+4 format like "60606-6604")
    const userPostalCode = _.get(values, `${prefix}postalCode`);
    const userZip5 = userPostalCode ? userPostalCode.split('-')[0].trim() : '';
    const isZipEqual = zip5 === userZip5;
    const isStateEqual = state === _.get(values, `${prefix}state`);
    // Normalize addresses for comparison (trim, uppercase, normalize whitespace)
    const normalizeAddress = (addr: string | null | undefined) => {
      if (!addr) return '';
      return addr
        .trim()
        .replace(/\s+/g, ' ')
        .toUpperCase();
    };
    const normalizedUspsAddress1 = normalizeAddress(uspsAddress1);
    const normalizedUserAddress1 = normalizeAddress(_.get(values, `${prefix}address1`));
    const normalizedUspsAddress2 = normalizeAddress(uspsAddress2);
    const normalizedUserAddress2 = normalizeAddress(_.get(values, `${prefix}address2`));
    // Since Smarty doesn't return address2 separately, only validate address1
    // Address2 validation is optional - if Smarty doesn't have it, don't fail validation
    const isAddressEqual =
      normalizedUspsAddress1 === normalizedUserAddress1 &&
      (!normalizedUspsAddress2 || normalizedUspsAddress2 === normalizedUserAddress2);
    const isAddressCorrect = !error && isCityEqual && isZipEqual && isStateEqual && isAddressEqual;

    // Set the flag based on the address comparison results
    isValidAddress = isAddressCorrect;
  }

  // Clear USPS storage if address fields are cleared
  if (!address1 || !city) {
    sessionStorage.setItem(`${prefix}uspsResponse`, 'undefined');
  }

  // If address has been provided, excluding postal code, country is US but the current address is not valid, then perform USPS verification to retrieve valid address items or errors
  if (
    (address1 && city && state && country === Country.UnitedStatesOfAmerica && !isValidAddress) ||
    (address1 && address2 && city && state && country === Country.UnitedStatesOfAmerica && !isValidAddress)
  ) {
    // Check if we already have a cached result for this exact address before making API call
    const addressKey = createAddressKey({ address1, address2, city, state, country });
    const cachedResponse = sessionStorage.getItem(`${prefix}uspsResponse`);

    let shouldMakeApiCall = true;
    if (cachedResponse && cachedResponse !== 'undefined') {
      try {
        const cached = JSON.parse(cachedResponse);
        // Check if cached result matches the current address
        if (
          cached.addressKey === addressKey ||
          (cached.uspsAddress1 &&
            cached.uspsAddress1.toUpperCase() === (address1 || '').trim().toUpperCase() &&
            cached.city &&
            cached.city.toUpperCase() === (city || '').trim().toUpperCase() &&
            cached.state &&
            cached.state.toUpperCase() === (state || '').trim().toUpperCase())
        ) {
          // We have a valid cached result - use it without making API call
          uspsResponseValue = cached;
          shouldMakeApiCall = false;
        }
      } catch (e) {
        // Invalid cached data, continue to make API call
      }
    }

    // Only make API call if we don't have a valid cached result
    if (shouldMakeApiCall) {
      // Set USPS Verification result to sessionStorage
      getUSPS(values, field, apiToken).then(result => {
        if (result) {
          // Store the address key with the result
          const resultWithKey = { ...result, addressKey };
          sessionStorage.setItem(`${prefix}uspsResponse`, JSON.stringify(resultWithKey));
          uspsResponseValue = resultWithKey;
        } else {
          uspsResponseValue = result;
        }
      });
    }
  }

  if (uspsResponseValue && address.country === Country.UnitedStatesOfAmerica) {
    const {
      city,
      state,
      zip5,
      error,
      uspsAddress1,
      uspsAddress2,
      isPoBox,
      isSecondaryInformationRequired,
      isSecondaryInformationNotRecognized,
    } = uspsResponseValue;
    if (error && address.address1 && address.city && address.state && address.postalCode) {
      return {
        [`${prefix}postalCode`]: `${error.description.trim()} Please review the entered address.`,
        [`${prefix}city`]: `${error.description.trim()} Please review the entered address.`,
        [`${prefix}state`]: `${error.description.trim()} Please review the entered address.`,
        [`${prefix}address1`]: `${error.description.trim()} Please review the entered address.`,
      };
    }

    if (!address.address1 || !address.city) {
      sessionStorage.setItem(`${prefix}uspsResponse`, 'undefined');
    }

    if (city && state && zip5) {
      // Formatting for error message display of USPS response
      const formattedCityCapitalized = _.capitalize(city);
      const startCaseCity = _.startCase(formattedCityCapitalized);
      const isCityEqual = _.toLower(startCaseCity) === _.toLower(_.get(values, `${prefix}city`));
      // Extract zip5 from postalCode (handle zip+4 format like "60606-6604")
      const userPostalCode = _.get(values, `${prefix}postalCode`);
      const userZip5 = userPostalCode ? userPostalCode.split('-')[0].trim() : '';
      const isZipEqual = zip5 === userZip5;
      const isStateEqual = state === _.get(values, `${prefix}state`);
      // Normalize addresses for comparison (trim, uppercase, normalize whitespace)
      const normalizeAddress = (addr: string | null | undefined) => {
        if (!addr) return '';
        return addr
          .trim()
          .replace(/\s+/g, ' ')
          .toUpperCase();
      };
      const normalizedUspsAddress1 = normalizeAddress(uspsAddress1);
      const normalizedUserAddress1 = normalizeAddress(_.get(values, `${prefix}address1`));
      const normalizedUspsAddress2 = normalizeAddress(uspsAddress2);
      const normalizedUserAddress2 = normalizeAddress(_.get(values, `${prefix}address2`));
      const isAddress1Equal = normalizedUspsAddress1 === normalizedUserAddress1;
      // Since Smarty doesn't return address2 separately, only validate if both exist
      // If Smarty doesn't have address2, don't validate user's address2
      const isAddress2Equal = normalizedUspsAddress2 ? normalizedUspsAddress2 === normalizedUserAddress2 : true;

      const formattedAddress1 = uspsAddress1
        .toLowerCase()
        .split(' ')
        .map((s: string) => s.charAt(0).toUpperCase() + s.substring(1))
        .join(' ');
      const formattedAddress2 = uspsAddress2
        ? uspsAddress2
            .toLowerCase()
            .split(' ')
            .map((s: string) => s.charAt(0).toUpperCase() + s.substring(1))
            .join(' ')
        : null;

      // Required Errors
      if (!address.postalCode) {
        return {
          [`${prefix}postalCode`]: getRequiredError(_.get(values, `${prefix}postalCode`)),
        };
      }

      if (!address.city) {
        return {
          [`${prefix}city`]: getRequiredError(_.get(values, `${prefix}city`)),
        };
      }

      // USPS Verification Errors
      let errors = {};

      // PO Box validation - if Smarty identifies this as a PO Box, show error
      if (isPoBox && !isTrustedContactAddress) {
        errors = {
          ...errors,
          [`${prefix}address1`]: 'PO Box addresses are not allowed for this address type.',
        };
      }

      // Secondary information (address2) required validation
      const userAddress2 = _.get(values, `${prefix}address2`);
      if (isSecondaryInformationRequired && !userAddress2) {
        errors = {
          ...errors,
          [`${prefix}address2`]: 'Secondary address information (apartment, suite, etc.) is required for this address.',
        };
      }

      // Secondary information not recognized warning
      if (isSecondaryInformationNotRecognized && userAddress2) {
        errors = {
          ...errors,
          [`${prefix}address2`]: 'The secondary address information provided was not recognized. Please verify or remove it.',
        };
      }

      if (!isCityEqual) {
        errors = {
          ...errors,
          [`${prefix}city`]: `Please verify city. USPS Provided City: ${startCaseCity}`,
        };
      }
      if (!isZipEqual) {
        errors = {
          ...errors,
          [`${prefix}postalCode`]: `Please verify the zip code. USPS Provided Zip Code: ${zip5}`,
        };
      }
      if (!isStateEqual) {
        errors = {
          ...errors,
          [`${prefix}state`]: `Please verify the state. USPS Provided State: ${state}`,
        };
      }
      // Only show address1 error if we haven't already shown a more specific error above
      if (!isAddress1Equal && !(errors as Record<string, string>)[`${prefix}address1`]) {
        errors = {
          ...errors,
          [`${prefix}address1`]: `Please utilize the USPS provided formatting: ${formattedAddress1}`,
        };
      }
      if (normalizedUspsAddress2 && !isAddress2Equal) {
        errors = {
          ...errors,
          [`${prefix}address2`]: `Please utilize the USPS provided formatting: ${formattedAddress2}`,
        };
      }
      return errors;
    }
  }
};

const validateAddress = (values: ApplicationModel, field?: string, validatePoBox = false, apiToken?: string) => {
  const prefix = `${field || 'address'}.`;
  const isMailing = _.toLower(prefix).includes('mailing');
  // are there other countries we need to require state for?
  const stateOrPostalCodeRequired = _.get(values, `${prefix}country`) === Country.UnitedStatesOfAmerica;
  const isTrustedContactAddress = field && field.toLowerCase().includes('trustedcontact');

  const address = {
    address1: _.get(values, `${prefix}address1`),
    address2: _.get(values, `${prefix}address2`),
    city: _.get(values, `${prefix}city`),
    state: _.get(values, `${prefix}state`),
    postalCode: _.get(values, `${prefix}postalCode`),
    country: _.get(values, `${prefix}country`),
  };

  // Get API token from store if not provided
  let token = apiToken;
  if (!token) {
    try {
      const state = DataStore.getState();
      token = state.ui.apiToken;
    } catch (err) {
      console.warn('Could not access store to get API token:', err);
    }
  }

  let uspsResponseFromStorage = getUspsResponseFromStorage(prefix);

  const uspsValidationErrors = validateWithUspsResponse(
    uspsResponseFromStorage,
    address,
    prefix,
    values,
    field || 'address',
    token
  );
  if (Object.keys(uspsValidationErrors || {}).length > 0) {
    return uspsValidationErrors || {};
  }

  if (!isMailing) {
    return {
      [`${prefix}country`]: getRequiredError(_.get(values, `${prefix}country`)),
      [`${prefix}address1`]:
        getRequiredError(_.get(values, `${prefix}address1`)) ||
        (!isTrustedContactAddress && getInvalidError(!RE_PO_BOX.test(_.get(values, `${prefix}address1`)))) ||
        getInvalidError(ASCII_AUDIT_RANGE.test(_.get(values, `${prefix}address1`))) ||
        getInvalidLengthError(_.get(values, `${prefix}address1`).length <= 30) ||
        (_.get(values, `${prefix}address1`) && getInvalidError(_.trim(_.get(values, `${prefix}address1`)) !== '')),
      [`${prefix}address2`]:
        (_.get(values, `${prefix}address2`) &&
          !isTrustedContactAddress &&
          getInvalidError(!RE_PO_BOX.test(_.get(values, `${prefix}address2`)))) ||
        (_.get(values, `${prefix}address2`) &&
          getInvalidError(ASCII_AUDIT_RANGE.test(_.get(values, `${prefix}address2`)))) ||
        (_.get(values, `${prefix}address2`) &&
          getInvalidLengthError(_.get(values, `${prefix}address2`).length <= 30)) ||
        (_.get(values, `${prefix}address2`) && getInvalidError(_.trim(_.get(values, `${prefix}address2`)) !== '')),
      [`${prefix}postalCode`]:
        getRequiredError(stateOrPostalCodeRequired ? _.get(values, `${prefix}postalCode`) : true) ||
        getInvalidError(stateOrPostalCodeRequired ? RE_POSTAL_CODE.test(_.get(values, `${prefix}postalCode`)) : true),
      [`${prefix}city`]: getRequiredError(_.get(values, `${prefix}city`)),
      [`${prefix}state`]: getRequiredError(stateOrPostalCodeRequired ? _.get(values, `${prefix}state`) : true),
    };
  } else if (isMailing) {
    return {
      [`${prefix}country`]: getRequiredError(_.get(values, `${prefix}country`)),
      [`${prefix}address1`]:
        getRequiredError(_.get(values, `${prefix}address1`)) ||
        (_.get(values, `${prefix}address1`) && getInvalidError(_.trim(_.get(values, `${prefix}address1`)) !== '')),
      [`${prefix}address2`]:
        _.get(values, `${prefix}address2`) && getInvalidError(_.trim(_.get(values, `${prefix}address2`)) !== ''),
      [`${prefix}postalCode`]: getRequiredError(
        stateOrPostalCodeRequired ? _.get(values, `${prefix}postalCode`) : true
      ),
      [`${prefix}city`]: getRequiredError(_.get(values, `${prefix}city`)),
      [`${prefix}state`]: getRequiredError(stateOrPostalCodeRequired ? _.get(values, `${prefix}state`) : true),
    };
  }
};

const validatePhones = (values: ApplicationModel, field?: string) => {
  const prefix = field ? `${field}.` : '';
  return _.reduce(
    _.get(values, `${prefix}phones`) || [{ type: undefined, phoneNumber: undefined }],
    (acc: { [key: string]: string | undefined }, phone: PhoneModel, i: number) => {
      return {
        ...acc,
        ...validatePhone(phone, `${prefix}phones[${i}]`, i === 0),
      };
    },
    {}
  );
};

const validatePhoneNumber = (value: string) => {
  // Correct the regex pattern to allow multiple digits, underscores, and hyphens
  var regex = /^[0-9_-]+$/;
  // Check if the value matches the regex and if its length (not including "-") is greater than 10
  return regex.test(value) && value.replace(/-/g, '').length >= 10 && value.replace(/-/g, '').length <= 50;
};

const validatePhone = (value: PhoneModel | undefined, field: string, validateAllFields = true) => {
  return {
    [`${field}.type`]: getRequiredError(validateAllFields || (value && value.phoneNumber) ? value && value.type : true),
    [`${field}.phoneNumber`]:
      getRequiredError(validateAllFields || (value && value.type) ? value && value.phoneNumber : true) ||
      getInvalidError(
        validateAllFields || (value && value.phoneNumber) ? value && validatePhoneNumber(value.phoneNumber) : true
      ),
  };
};

const trimName = (name: string | undefined): string => {
  return (name || '').trim();
};

const validatePerson = (
  values: ApplicationModel,
  field: string | undefined,
  contactType?: PersonType | false,
  validateName = true
) => {
  const prefix = field ? `${field}.` : '';

  const firstName = trimName(_.get(values, `${prefix}firstName`));
  const middleName = trimName(_.get(values, `${prefix}middleName`));
  const lastName = trimName(_.get(values, `${prefix}lastName`));

  return {
    [`${prefix}firstName`]:
      (validateName && (getRequiredError(firstName) || getInvalidError(isValidPersonName(firstName)))) ||
      getInvalidLengthError(firstName.length <= 40) ||
      getInvalidLegalNameError(firstName.length + lastName.length <= 60),
    [`${prefix}middleName`]:
      middleName &&
      (getInvalidError(isValidPersonName(middleName)) || getInvalidLengthError(middleName.length <= 20)),
    [`${prefix}lastName`]:
      (validateName && (getRequiredError(lastName) || getInvalidError(isValidPersonName(lastName)))) ||
      getInvalidLengthError(lastName.length <= 40) ||
      getInvalidLegalNameError(firstName.length + lastName.length <= 60),
    // suffix should either be an empty string, "None", or a string of letters
    [`${prefix}suffix`]:
      _.get(values, `${prefix}suffix`) &&
      _.get(values, `${prefix}suffix`) !== 'None' &&
      (getInvalidError(RE_VALID_SUFFIX.test(_.get(values, `${prefix}suffix`))) ||
        getInvalidLengthError(_.get(values, `${prefix}suffix`).length <= 10) ||
        getInvalidError(_.get(values, `${prefix}suffix`) && !RE_PREFIX_PATTERN.test(_.get(values, `${prefix}suffix`)))),

    ...(contactType !== PersonType.AccountHolder ? validateAddress(values, `${prefix}address`) : {}),
    ...(contactType === PersonType.AccountHolder
      ? {
          [`${prefix}email`]: getEmailError(_.get(values, `${prefix}email`)),
          ...(_.get(values, `${prefix}email`)
            ? {
                [`${prefix}emailConfirm`]:
                  getRequiredError(_.get(values, `${prefix}emailConfirm`)) ||
                  getInvalidError(_.isEqual(_.get(values, `${prefix}email`), _.get(values, `${prefix}emailConfirm`))),
              }
            : undefined),
        }
      : {}),
  };
};

const validatePersonDetails = (
  values: ApplicationModel,
  field: string | undefined,
  validateName = true,
  duplicateAccountCheck?: {
    isFetching: boolean;
    error?: boolean;
    errorMessage?: string[] | string | undefined;
  }
) => {
  const prefix = field ? `${field}.` : '';
  const { isFetching, error, errorMessage } = duplicateAccountCheck || {
    isFetching: false,
    error: false,
    errorMessage: undefined,
  };

  const isForeign =
    values.primaryAccountHolder &&
    values.primaryAccountHolder.citizenshipCountry &&
    values.primaryAccountHolder.citizenshipCountry !== Country.UnitedStatesOfAmerica;
  if (values.type === 'Ugma' && prefix === 'secondaryAccountHolder.') {
    return {
      ...validatePerson(values, field, false, validateName),
      ...validateDate(values, `${prefix}dateOfBirth`, false),
      [`${prefix}ssn`]:
        (!isForeign &&
          (getRequiredError(_.get(values, `${prefix}ssn`)) ||
            getInvalidError(RE_SSN.test(_.get(values, `${prefix}ssn`))))) ||
        error
          ? errorMessage
          : undefined,
    };
  }
  return {
    ...validatePerson(values, field, false, validateName),
    ...validateDate(values, `${prefix}dateOfBirth`, true),
    [`${prefix}ssn`]:
      (!isForeign &&
        (getRequiredError(_.get(values, `${prefix}ssn`)) ||
          getInvalidError(RE_SSN.test(_.get(values, `${prefix}ssn`))))) ||
      error
        ? errorMessage
        : undefined,
  };
};
const validateBeneficiaryPercentages = (
  beneficiaries: BeneficiaryModel[]
): Record<string, ErrorType | string | undefined> => {
  const primaryBeneficiaries = beneficiaries.filter(b => b.beneficiaryAccountType === BeneficiaryAccountType.Primary);
  const contingentBeneficiaries = beneficiaries.filter(
    b => b.beneficiaryAccountType === BeneficiaryAccountType.Contingent
  );
  const totalPrimaryPercent = _.sumBy(primaryBeneficiaries, b => _.toNumber(b.percent));
  const totalContingentPercent = _.sumBy(contingentBeneficiaries, b => _.toNumber(b.percent));

  // Block any contingent beneficiaries if no primary exists
  if (contingentBeneficiaries.length > 0) {
    if (primaryBeneficiaries.length === 0) {
      const errorMessage = 'At least one primary beneficiary is required before adding contingent beneficiaries.';
      return {
        'beneficiaries[0].beneficiaryAccountType': errorMessage,
        'beneficiaries[0].percent': errorMessage,
      };
    }

    if (totalPrimaryPercent !== 100) {
      const errorMessage = 'Primary beneficiaries must total 100% before adding contingent beneficiaries.';
      return {
        'beneficiaries[0].beneficiaryAccountType': errorMessage,
        'beneficiaries[0].percent': errorMessage,
      };
    }

    if (totalContingentPercent !== 100) {
      const errors: Record<string, string> = {};
      contingentBeneficiaries.forEach((b, i) => {
        const index = beneficiaries.indexOf(b);
        errors[`beneficiaries[${index}].percent`] = 'Contingent beneficiaries must total 100%.';
      });
      return errors;
    }
  }

  // Validate primary percentages
  if (primaryBeneficiaries.length > 0 && totalPrimaryPercent !== 100) {
    const errors: Record<string, string> = {};
    primaryBeneficiaries.forEach((b, i) => {
      const index = beneficiaries.indexOf(b);
      errors[`beneficiaries[${index}].percent`] = 'Primary beneficiaries must total 100%.';
    });
    return errors;
  }

  return {};
};
